/**
 * Auth guard state machine — pure module.
 *
 * `useAuthGuardState` (auth-core.hook.ts) owns the React lifecycle (effects,
 * cancellation); this module owns the transitions between them. No React, no
 * globals, no side effects — `loginWithRedirect` and the claims fetch are
 * triggered by the hook in reaction to a phase change, never from here.
 */

import type { AccessDecision } from "./auth-access";
import type { AuthGuardState } from "./auth-core.hook";

export type AuthGuardPhase =
  | { name: "checking-session" }
  | { name: "redirecting"; returnTo: string }
  | { name: "checking-claims" }
  | { name: "granted" }
  | { name: "denied" }
  | { name: "failed" };

export type AuthGuardEvent =
  | { type: "SESSION_CONFIRMED" }
  | { type: "SILENT_AUTH_FAILED"; returnTo: string }
  | { type: "CLAIMS_RESOLVED"; decision: AccessDecision }
  /** Auth0 reported an error handling its callback. Final: never answered with another redirect. */
  | { type: "SIGN_IN_FAILED" };

export const initialAuthGuardPhase: AuthGuardPhase = { name: "checking-session" };

/** Transitions are only valid from the phase that can actually produce them; any other event is a no-op (returns the same phase reference, so React skips the re-render). */
export function authGuardReducer(phase: AuthGuardPhase, event: AuthGuardEvent): AuthGuardPhase {
  switch (event.type) {
    case "SESSION_CONFIRMED":
      return phase.name === "checking-session" ? { name: "checking-claims" } : phase;
    case "SILENT_AUTH_FAILED":
      return phase.name === "checking-session"
        ? { name: "redirecting", returnTo: event.returnTo }
        : phase;
    case "CLAIMS_RESOLVED":
      return phase.name === "checking-claims" ? { name: event.decision } : phase;
    case "SIGN_IN_FAILED":
      return phase.name === "checking-session" ? { name: "failed" } : phase;
    default:
      return phase;
  }
}

/**
 * `isLoading` is continuous state from the Auth0 SDK, not a discrete event —
 * it overrides the phase the same way it did in the pre-reducer `if`-chain,
 * including flipping an already-`granted` phase back to `"loading"` during a
 * token refresh.
 */
export function deriveGuardState(phase: AuthGuardPhase, isLoading: boolean): AuthGuardState {
  if (isLoading) return "loading";
  if (phase.name === "granted" || phase.name === "denied" || phase.name === "failed") {
    return phase.name;
  }
  return "loading";
}
